import { ChevronDown, Loader2, Pencil, Plus, Search, Star, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePullToRefresh } from '../hooks/usePullToRefresh';
import {
  createExpense,
  deleteExpense,
  getCategories,
  getExpenses,
  getRecurring,
  updateExpense,
  type Category,
  type Expense,
  type RecurringTemplate,
} from '../api';
import { CategoryPicker } from '../components/CategoryPicker';
import { RecurringMonthModal } from '../components/RecurringMonthModal';
import { CategoryIcon } from '../categoryIcons';
import { formatMonthTitle, localDateYmd, localTimeHm, monthBounds } from '../utils/dates';
import { amountToInputString, eur } from '../utils/money';

const VIEWED_MONTH_KEY = 'spese-viewed-month';

function readStoredViewedMonth(): { year: number; monthIndex: number } | null {
  try {
    const s = sessionStorage.getItem(VIEWED_MONTH_KEY);
    if (!s || !/^\d{4}-\d{2}$/.test(s)) return null;
    const [ys, mss] = s.split('-');
    const y = Number(ys);
    const mi = Number(mss) - 1;
    if (!Number.isFinite(y) || !Number.isFinite(mi) || mi < 0 || mi > 11) return null;
    return { year: y, monthIndex: mi };
  } catch {
    return null;
  }
}

function persistViewedMonth(year: number, monthIndex: number) {
  try {
    sessionStorage.setItem(
      VIEWED_MONTH_KEY,
      `${year}-${String(monthIndex + 1).padStart(2, '0')}`
    );
  } catch {
    /* ignore */
  }
}

function initialViewedMonth(): { year: number; monthIndex: number } {
  const stored = readStoredViewedMonth();
  if (stored) return stored;
  const n = new Date();
  return { year: n.getFullYear(), monthIndex: n.getMonth() };
}

export default function HomePage() {
  const [year, setYear] = useState(() => initialViewedMonth().year);
  const [monthIndex, setMonthIndex] = useState(() => initialViewedMonth().monthIndex);

  const [categories, setCategories] = useState<Category[]>([]);
  const [recurringTemplates, setRecurringTemplates] = useState<RecurringTemplate[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [recurringModalOpen, setRecurringModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const [addOpen, setAddOpen] = useState(false);
  const [addBusy, setAddBusy] = useState(false);
  const [addErr, setAddErr] = useState('');
  const [addAmount, setAddAmount] = useState('');
  const [addCategoryId, setAddCategoryId] = useState('');
  const [addDate, setAddDate] = useState(() => localDateYmd());
  const [addTime, setAddTime] = useState(() => localTimeHm());
  const [addNote, setAddNote] = useState('');

  const [editOpen, setEditOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [editBusy, setEditBusy] = useState(false);
  const [editErr, setEditErr] = useState('');
  const [editAmount, setEditAmount] = useState('');
  const [editCategoryId, setEditCategoryId] = useState('');
  const [editDate, setEditDate] = useState('');
  const [editTime, setEditTime] = useState('');
  const [editNote, setEditNote] = useState('');

  const [deleteTarget, setDeleteTarget] = useState<Expense | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteErr, setDeleteErr] = useState('');

  const [searchQuery, setSearchQuery] = useState('');
  const [flowFilter, setFlowFilter] = useState<'all' | 'in' | 'out'>('all');
  const [categoryFilterId, setCategoryFilterId] = useState('');

  const { dateFrom, dateTo } = useMemo(() => monthBounds(year, monthIndex), [year, monthIndex]);

  const sortedCategories = useMemo(
    () =>
      categories.slice().sort((a, b) => {
        const ta = a.type === 'in' ? 0 : 1;
        const tb = b.type === 'in' ? 0 : 1;
        if (ta !== tb) return ta - tb;
        return a.name.localeCompare(b.name, 'it');
      }),
    [categories]
  );

  const categoryById = useMemo(() => {
    const m = new Map<string, Category>();
    for (const c of categories) m.set(c.categoryId, c);
    return m;
  }, [categories]);

  const fetchHomeData = useCallback(async (from: string, to: string) => {
    setErr('');
    setLoading(true);
    try {
      const [catRes, recRes, expRes] = await Promise.all([
        getCategories(),
        getRecurring(),
        getExpenses({
          dateFrom: from,
          dateTo: to,
          limit: '10000',
        }),
      ]);
      setCategories(catRes.categories);
      setRecurringTemplates(recRes.templates);
      setExpenses(expRes.expenses);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Errore caricamento');
    } finally {
      setLoading(false);
    }
  }, []);

  const load = useCallback(() => fetchHomeData(dateFrom, dateTo), [fetchHomeData, dateFrom, dateTo]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setSearchQuery('');
    setFlowFilter('all');
    setCategoryFilterId('');
  }, [year, monthIndex]);

  const viewedMonthRef = useRef({ year, monthIndex });
  viewedMonthRef.current = { year, monthIndex };

  const refreshViewedMonth = useCallback(() => {
    const { year: y, monthIndex: mi } = viewedMonthRef.current;
    const { dateFrom: df, dateTo: dt } = monthBounds(y, mi);
    return fetchHomeData(df, dt);
  }, [fetchHomeData]);

  const activeRecurring = useMemo(
    () => recurringTemplates.filter((t) => t.active !== false),
    [recurringTemplates]
  );

  const recurringMonthComplete = useMemo(() => {
    if (activeRecurring.length === 0) return true;
    const present = new Set(
      expenses.filter((ex) => ex.recurringId).map((ex) => ex.recurringId as string)
    );
    return activeRecurring.every((t) => present.has(t.recurringId));
  }, [activeRecurring, expenses]);

  const anyModalOpen = addOpen || editOpen || deleteTarget != null || recurringModalOpen;
  const { pullPx, progress, refreshing: ptrRefreshing } = usePullToRefresh(
    useCallback(() => void refreshViewedMonth(), [refreshViewedMonth]),
    { enabled: !anyModalOpen }
  );

  function openAdd() {
    const d = new Date();
    setAddErr('');
    setAddAmount('');
    setAddNote('');
    setAddDate(localDateYmd(d));
    setAddTime(localTimeHm(d));
    const outs = categories.filter((c) => c.type === 'out');
    setAddCategoryId(outs[0]?.categoryId ?? categories[0]?.categoryId ?? '');
    setAddOpen(true);
  }

  async function onSubmitAdd(e: React.FormEvent) {
    e.preventDefault();
    setAddErr('');
    const raw = addAmount.replace(',', '.').trim();
    const amount = parseFloat(raw);
    if (!Number.isFinite(amount) || amount <= 0) {
      setAddErr('Inserisci un importo valido.');
      return;
    }
    if (!addCategoryId) {
      setAddErr('Scegli una categoria.');
      return;
    }
    setAddBusy(true);
    try {
      await createExpense({
        amount,
        categoryId: addCategoryId,
        date: addDate,
        ...(addTime.trim() ? { time: addTime.trim() } : {}),
        ...(addNote.trim() ? { note: addNote.trim() } : {}),
        currency: 'EUR',
      });
      setAddOpen(false);
      await load();
    } catch (x) {
      setAddErr(x instanceof Error ? x.message : 'Errore');
    } finally {
      setAddBusy(false);
    }
  }

  function openEdit(ex: Expense) {
    setAddOpen(false);
    setDeleteTarget(null);
    setEditErr('');
    setEditId(ex.expenseId);
    setEditAmount(amountToInputString(Number(ex.amount) || 0));
    setEditCategoryId(ex.categoryId);
    setEditDate(ex.date);
    const t = ex.time?.trim() || '';
    setEditTime(t.length >= 5 ? t.slice(0, 5) : t);
    setEditNote(ex.note ?? '');
    setEditOpen(true);
  }

  async function onSubmitEdit(e: React.FormEvent) {
    e.preventDefault();
    if (!editId) return;
    setEditErr('');
    const raw = editAmount.replace(',', '.').trim();
    const amount = parseFloat(raw);
    if (!Number.isFinite(amount) || amount <= 0) {
      setEditErr('Inserisci un importo valido.');
      return;
    }
    if (!editCategoryId) {
      setEditErr('Scegli una categoria.');
      return;
    }
    setEditBusy(true);
    try {
      const payload: Parameters<typeof updateExpense>[1] = {
        amount,
        categoryId: editCategoryId,
        date: editDate,
        note: editNote.trim(),
        currency: 'EUR',
      };
      if (editTime.trim()) {
        payload.time = editTime.trim();
      }
      await updateExpense(editId, payload);
      setEditOpen(false);
      setEditId(null);
      await load();
    } catch (x) {
      setEditErr(x instanceof Error ? x.message : 'Errore');
    } finally {
      setEditBusy(false);
    }
  }

  function openDeleteConfirm(ex: Expense) {
    setAddOpen(false);
    setEditOpen(false);
    setDeleteErr('');
    setDeleteTarget(ex);
  }

  async function onConfirmDelete() {
    if (!deleteTarget) return;
    setDeleteErr('');
    setDeleteBusy(true);
    try {
      await deleteExpense(deleteTarget.expenseId);
      setDeleteTarget(null);
      await load();
    } catch (e) {
      setDeleteErr(e instanceof Error ? e.message : 'Eliminazione non riuscita');
    } finally {
      setDeleteBusy(false);
    }
  }

  const { entrate, uscite } = useMemo(() => {
    let entrate = 0;
    let uscite = 0;
    for (const ex of expenses) {
      const cat = categoryById.get(ex.categoryId);
      const flow = cat?.type === 'in' ? 'in' : 'out';
      const amt = Number(ex.amount) || 0;
      if (flow === 'in') entrate += amt;
      else uscite += amt;
    }
    return { entrate, uscite };
  }, [expenses, categoryById]);

  const saldo = entrate - uscite;

  const outCategories = useMemo(
    () =>
      categories
        .filter((c) => c.type === 'out')
        .sort((a, b) => a.name.localeCompare(b.name, 'it')),
    [categories]
  );

  const searchNorm = searchQuery.trim().toLowerCase();
  const hasListFilter =
    searchNorm.length > 0 || flowFilter !== 'all' || categoryFilterId !== '';
  const visibleExpenses = useMemo(() => {
    return expenses.filter((ex) => {
      if (flowFilter !== 'all') {
        const flow = categoryById.get(ex.categoryId)?.type === 'in' ? 'in' : 'out';
        if (flow !== flowFilter) return false;
      }
      if (flowFilter === 'out' && categoryFilterId && ex.categoryId !== categoryFilterId) {
        return false;
      }
      if (searchNorm && !(ex.note ?? '').toLowerCase().includes(searchNorm)) return false;
      return true;
    });
  }, [expenses, categoryById, searchNorm, flowFilter, categoryFilterId]);

  const visibleSaldo = useMemo(() => {
    let total = 0;
    for (const ex of visibleExpenses) {
      const flow = categoryById.get(ex.categoryId)?.type === 'in' ? 'in' : 'out';
      const amt = Number(ex.amount) || 0;
      if (flow === 'in') total += amt;
      else total -= amt;
    }
    return total;
  }, [visibleExpenses, categoryById]);

  function onFlowFilterChange(next: 'all' | 'in' | 'out') {
    setFlowFilter(next);
    if (next !== 'out') setCategoryFilterId('');
  }

  function prevMonth() {
    const d = new Date(year, monthIndex - 1, 1);
    const ny = d.getFullYear();
    const nm = d.getMonth();
    persistViewedMonth(ny, nm);
    setYear(ny);
    setMonthIndex(nm);
  }

  function nextMonth() {
    const d = new Date(year, monthIndex + 1, 1);
    const ny = d.getFullYear();
    const nm = d.getMonth();
    persistViewedMonth(ny, nm);
    setYear(ny);
    setMonthIndex(nm);
  }

  const monthSwipeRef = useRef<{ x: number; y: number; id: number } | null>(null);

  function onMonthSwipeStart(e: React.TouchEvent) {
    if (anyModalOpen) return;
    const t = e.touches[0];
    monthSwipeRef.current = { x: t.clientX, y: t.clientY, id: t.identifier };
  }

  function onMonthSwipeEnd(e: React.TouchEvent) {
    if (anyModalOpen) {
      monthSwipeRef.current = null;
      return;
    }
    const start = monthSwipeRef.current;
    monthSwipeRef.current = null;
    if (!start) return;
    const t = Array.from(e.changedTouches).find((c) => c.identifier === start.id);
    if (!t) return;
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    const minDx = 56;
    if (Math.abs(dx) < minDx) return;
    if (Math.abs(dy) > Math.abs(dx) * 0.72) return;
    if (dx > 0) prevMonth();
    else nextMonth();
  }

  function onMonthSwipeCancel() {
    monthSwipeRef.current = null;
  }

  return (
    <div className="home">
      {(pullPx > 3 || ptrRefreshing) && (
        <div className="ptr-wrap" aria-live={ptrRefreshing ? 'polite' : 'off'}>
          <div
            className="ptr-chip"
            style={{
              transform: `translateY(${Math.min(pullPx * 0.55, 44) - 28}px)`,
              opacity: ptrRefreshing ? 1 : Math.min(1, pullPx / 26),
            }}
          >
            {ptrRefreshing ? (
              <Loader2 className="ptr-loader" size={22} strokeWidth={2} aria-label="Aggiornamento" />
            ) : (
              <ChevronDown
                size={22}
                strokeWidth={2}
                className="ptr-chevron"
                style={{ transform: `rotate(${progress * 180}deg)` }}
                aria-hidden
              />
            )}
          </div>
        </div>
      )}

      <div className="month-nav">
        <button type="button" className="month-nav-btn" onClick={prevMonth} aria-label="Mese precedente">
          ‹
        </button>
        <h2 className="month-title">{formatMonthTitle(year, monthIndex)}</h2>
        <button type="button" className="month-nav-btn" onClick={nextMonth} aria-label="Mese successivo">
          ›
        </button>
      </div>

      {err && <p className="home-err">{err}</p>}

      <div className="summary-row">
        <div className="summary-card summary-in">
          <div className="summary-label">Entrate</div>
          <div className="summary-value">{eur(entrate)}</div>
        </div>
        <div className="summary-card summary-out">
          <div className="summary-label">Uscite</div>
          <div className="summary-value">{eur(uscite)}</div>
        </div>
        <div className={`summary-card summary-balance ${saldo >= 0 ? 'pos' : 'neg'}`}>
          <div className="summary-label">Saldo</div>
          <div className="summary-value">{eur(saldo)}</div>
        </div>
      </div>

      {addOpen && (
        <div
          className="modal-backdrop"
          role="presentation"
          onClick={(ev) => {
            if (ev.target === ev.currentTarget) setAddOpen(false);
          }}
        >
          <div className="modal-panel" role="dialog" aria-labelledby="add-expense-title">
            <h3 id="add-expense-title" className="modal-title">
              Nuovo movimento
            </h3>
            <form onSubmit={onSubmitAdd}>
              <div className="field">
                <label htmlFor="add-amount">Importo (€)</label>
                <input
                  id="add-amount"
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  placeholder="es. 12,50"
                  value={addAmount}
                  onChange={(e) => setAddAmount(e.target.value)}
                  required
                />
              </div>
              <div className="field">
                <label htmlFor="add-cat">Categoria</label>
                <CategoryPicker
                  id="add-cat"
                  categories={sortedCategories}
                  value={addCategoryId}
                  onChange={setAddCategoryId}
                  allowEmpty
                />
              </div>
              <div className="field-row">
                <div className="field flex-1">
                  <label htmlFor="add-date">Data</label>
                  <input
                    id="add-date"
                    type="date"
                    value={addDate}
                    onChange={(e) => setAddDate(e.target.value)}
                    required
                  />
                </div>
                <div className="field w-time">
                  <label htmlFor="add-time">Ora (opz.)</label>
                  <input
                    id="add-time"
                    type="time"
                    value={addTime}
                    onChange={(e) => setAddTime(e.target.value)}
                  />
                </div>
              </div>
              <div className="field">
                <label htmlFor="add-note">Nota (opz.)</label>
                <input
                  id="add-note"
                  type="text"
                  value={addNote}
                  onChange={(e) => setAddNote(e.target.value)}
                  maxLength={500}
                />
              </div>
              {addErr && <p className="field-err">{addErr}</p>}
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setAddOpen(false)} disabled={addBusy}>
                  Annulla
                </button>
                <button type="submit" className="btn-primary btn-primary-inline" disabled={addBusy}>
                  {addBusy ? '…' : 'Salva'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editOpen && editId && (
        <div
          className="modal-backdrop"
          role="presentation"
          onClick={(ev) => {
            if (ev.target === ev.currentTarget && !editBusy) {
              setEditOpen(false);
              setEditId(null);
            }
          }}
        >
          <div className="modal-panel" role="dialog" aria-labelledby="edit-expense-title">
            <h3 id="edit-expense-title" className="modal-title">
              Modifica movimento
            </h3>
            <form onSubmit={onSubmitEdit}>
              <div className="field">
                <label htmlFor="edit-amount">Importo (€)</label>
                <input
                  id="edit-amount"
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  value={editAmount}
                  onChange={(e) => setEditAmount(e.target.value)}
                  required
                />
              </div>
              <div className="field">
                <label htmlFor="edit-cat">Categoria</label>
                <CategoryPicker
                  id="edit-cat"
                  categories={sortedCategories}
                  value={editCategoryId}
                  onChange={setEditCategoryId}
                />
              </div>
              <div className="field-row">
                <div className="field flex-1">
                  <label htmlFor="edit-date">Data</label>
                  <input
                    id="edit-date"
                    type="date"
                    value={editDate}
                    onChange={(e) => setEditDate(e.target.value)}
                    required
                  />
                </div>
                <div className="field w-time">
                  <label htmlFor="edit-time">Ora (opz.)</label>
                  <input
                    id="edit-time"
                    type="time"
                    value={editTime}
                    onChange={(e) => setEditTime(e.target.value)}
                  />
                </div>
              </div>
              <div className="field">
                <label htmlFor="edit-note">Nota (opz.)</label>
                <input
                  id="edit-note"
                  type="text"
                  value={editNote}
                  onChange={(e) => setEditNote(e.target.value)}
                  maxLength={500}
                />
              </div>
              {editErr && <p className="field-err">{editErr}</p>}
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setEditOpen(false);
                    setEditId(null);
                  }}
                  disabled={editBusy}
                >
                  Annulla
                </button>
                <button type="submit" className="btn-primary btn-primary-inline" disabled={editBusy}>
                  {editBusy ? '…' : 'Salva'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div
          className="modal-backdrop modal-backdrop--confirm"
          role="presentation"
          onClick={(ev) => {
            if (ev.target === ev.currentTarget && !deleteBusy) setDeleteTarget(null);
          }}
        >
          <div className="modal-panel modal-panel--confirm" role="dialog" aria-labelledby="delete-expense-title">
            <div className="confirm-delete-visual" aria-hidden>
              <Trash2 size={28} strokeWidth={1.75} />
            </div>
            <h3 id="delete-expense-title" className="modal-title modal-title--center">
              Eliminare questo movimento?
            </h3>
            <p className="confirm-delete-lead">
              L’operazione non può essere annullata. Stai per rimuovere:
            </p>
            <div className="confirm-delete-card">
              <p className="confirm-delete-amount">
                {categoryById.get(deleteTarget.categoryId)?.type === 'in' ? '+' : '−'}
                {eur(Math.abs(Number(deleteTarget.amount) || 0))}
              </p>
              <p className="confirm-delete-meta">
                {deleteTarget.date}
                {deleteTarget.time ? ` · ${deleteTarget.time}` : ''}
                {' · '}
                {categoryById.get(deleteTarget.categoryId)?.name ?? deleteTarget.categoryId}
              </p>
              {deleteTarget.note ? <p className="confirm-delete-note">“{deleteTarget.note}”</p> : null}
            </div>
            {deleteErr ? <p className="field-err confirm-delete-err">{deleteErr}</p> : null}
            <div className="modal-actions modal-actions--stack-sm">
              <button
                type="button"
                className="btn-danger"
                onClick={() => void onConfirmDelete()}
                disabled={deleteBusy}
              >
                {deleteBusy ? '…' : 'Elimina'}
              </button>
              <button
                type="button"
                className="btn-secondary btn-secondary-ghost"
                onClick={() => setDeleteTarget(null)}
                disabled={deleteBusy}
              >
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}

      <RecurringMonthModal
        open={recurringModalOpen}
        year={year}
        monthIndex={monthIndex}
        templates={recurringTemplates}
        categoryById={categoryById}
        expenses={expenses}
        onClose={() => setRecurringModalOpen(false)}
        onSaved={() => void load()}
      />

      <div
        className="home-swipe-month"
        onTouchStart={onMonthSwipeStart}
        onTouchEnd={onMonthSwipeEnd}
        onTouchCancel={onMonthSwipeCancel}
      >
        <h3 className="list-heading">
          Movimenti del mese (
          {hasListFilter ? `${visibleExpenses.length} di ${expenses.length}` : visibleExpenses.length}
          ){!loading && (
            <span className="list-heading-saldo">
              {' '}
              · Saldo {visibleSaldo >= 0 ? '+' : '−'}
              {eur(Math.abs(visibleSaldo))}
            </span>
          )}
        </h3>
        <div className="list-toolbar">
          <label className="expense-search">
            <Search className="expense-search-icon" size={18} strokeWidth={2} aria-hidden />
            <input
              type="search"
              className="expense-search-input"
              placeholder="Cerca nella nota…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              disabled={loading}
              aria-label="Cerca per nota"
              autoComplete="off"
              enterKeyHint="search"
            />
          </label>
          <select
            className="expense-flow-filter"
            value={flowFilter}
            onChange={(e) => onFlowFilterChange(e.target.value as 'all' | 'in' | 'out')}
            disabled={loading}
            aria-label="Filtra per tipologia"
          >
            <option value="all">Tutte</option>
            <option value="in">Entrate</option>
            <option value="out">Uscite</option>
          </select>
          <button
            type="button"
            className={`btn-recurring-star${recurringMonthComplete ? ' btn-recurring-star--complete' : ''}`}
            onClick={() => setRecurringModalOpen(true)}
            disabled={loading}
            aria-label={
              recurringMonthComplete
                ? 'Movimenti ricorrenti: tutti inseriti per questo mese'
                : 'Movimenti ricorrenti: inserimenti mancanti per questo mese'
            }
          >
            <Star
              size={20}
              strokeWidth={2}
              className="btn-recurring-star-icon"
              aria-hidden
              fill={recurringMonthComplete ? 'currentColor' : 'none'}
            />
          </button>
          <button
            type="button"
            className="btn-add-expense btn-add-expense--icon"
            onClick={openAdd}
            disabled={loading || categories.length === 0}
            aria-label="Nuova spesa o entrata"
          >
            <Plus size={20} strokeWidth={2} aria-hidden />
          </button>
        </div>

        {flowFilter === 'out' && (
          <select
            className="expense-category-filter"
            value={categoryFilterId}
            onChange={(e) => setCategoryFilterId(e.target.value)}
            disabled={loading || outCategories.length === 0}
            aria-label="Filtra per categoria"
          >
            <option value="">Tutte le categorie</option>
            {outCategories.map((c) => (
              <option key={c.categoryId} value={c.categoryId}>
                {c.name}
              </option>
            ))}
          </select>
        )}

        {loading ? (
          <p className="home-muted">Caricamento…</p>
        ) : expenses.length === 0 ? (
          <p className="home-muted">Nessuna spesa o entrata in questo mese.</p>
        ) : visibleExpenses.length === 0 ? (
          <p className="home-muted">Nessun movimento corrisponde ai filtri.</p>
        ) : (
          <ul className="expense-list">
            {visibleExpenses.map((ex) => {
              const cat = categoryById.get(ex.categoryId);
              const isIn = cat?.type === 'in';
              const catName = cat?.name ?? ex.categoryId;
              const creatorUrl = ex.creatorAvatarUrl?.trim();
              const creatorLabel = ex.creatorDisplayName || 'Utente';
              const isRecurring = Boolean(ex.recurringId);
              return (
                <li
                  key={ex.expenseId}
                  className={`expense-row${isRecurring ? ' expense-row--recurring' : ''}`}
                >
                  <div className="expense-icons">
                    <span className="expense-creator-wrap" title={creatorLabel}>
                      {creatorUrl && (creatorUrl.startsWith('http') || creatorUrl.startsWith('data:')) ? (
                        <img src={creatorUrl} alt="" className="expense-creator-avatar" />
                      ) : (
                        <span className="expense-creator-initial">
                          {(creatorLabel.charAt(0) || '?').toUpperCase()}
                        </span>
                      )}
                    </span>
                    <span className="expense-cat-icon" title={catName}>
                      <CategoryIcon iconId={cat?.icon} size={22} className="expense-cat-svg" />
                    </span>
                  </div>
                  <div className="expense-main">
                    <span className="expense-date">
                      {ex.date}
                      {ex.time ? ` · ${ex.time}` : ''}
                    </span>
                    {ex.note ? <span className="expense-note">{ex.note}</span> : null}
                  </div>
                  <div className="expense-end">
                    <div className={`expense-amount ${isIn ? 'in' : 'out'}`}>
                      {isIn ? '+' : '−'}
                      {eur(Math.abs(Number(ex.amount) || 0))}
                    </div>
                    <div className="expense-actions">
                      {isRecurring ? (
                        <span className="expense-recurring-badge" title="Utenza ricorrente">
                          <Star size={17} strokeWidth={2} aria-hidden fill="currentColor" />
                        </span>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="icon-btn"
                            onClick={() => openEdit(ex)}
                            aria-label={`Modifica movimento del ${ex.date}`}
                          >
                            <Pencil size={17} strokeWidth={2} aria-hidden />
                          </button>
                          <button
                            type="button"
                            className="icon-btn icon-btn--danger"
                            onClick={() => openDeleteConfirm(ex)}
                            aria-label={`Elimina movimento del ${ex.date}`}
                          >
                            <Trash2 size={17} strokeWidth={2} aria-hidden />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
